import { useCallback, useState } from 'react';

import { AutoFixHigh } from '@mui/icons-material';
import { Stack } from '@mui/material';

import ContentSelector from 'domains/campaigns/components/CustomCampaign/components/ContentSelector';
import SubjectLinesDialog from 'domains/campaigns/components/SubjectLinesDialog';
import { EditContentPayload } from 'domains/campaigns/state/createCampaignSlice';
import { ContentFields, CampaignChannels } from 'domains/campaigns/types';
import { SenderProfile } from 'domains/content/types';
import IconButton from 'domains/core/components/IconButton';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import TextField from 'domains/core/components/TextField';
import useSenderProfiles from 'hooks/queries/useSenderProfiles';
import useTemplates from 'hooks/queries/useTemplates';
import useTemplateSuggestedSubjectLines from 'hooks/queries/useTemplateSuggestedSubjectLines';

type Props = {
    contentFields: ContentFields;
    nodeId: string;
    editContent: (contentFields: EditContentPayload) => void;
    contentPreviewNodeId: string;
    setContentPreviewNodeId: (contentPreviewNodeId: string) => void;
};

const CurationContentForm = ({
    contentFields,
    nodeId,
    editContent,
    contentPreviewNodeId,
    setContentPreviewNodeId,
}: Props) => {
    const { preHeader, senderProfileId, subjectLine, templateId } = contentFields;
    const [isSubjectLinesDialogOpen, setIsSubjectLinesDialogOpen] = useState(false);

    const { isLoading: isSenderProfilesLoading, data: senderProfiles = [] } = useSenderProfiles();
    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates();

    const { data: suggestedSubjectLines } = useTemplateSuggestedSubjectLines({
        templateId,
        reactQueryOptions: {
            enabled: !!templateId,
        },
    });

    const handleContentChange = useCallback(
        (contentId: number | null) => {
            if (contentId) {
                // Find the selected template to get all its properties
                const selectedTemplate = templates.find((template) => template.id === contentId);
                if (selectedTemplate) {
                    editContent({
                        contentFields: {
                            ...contentFields,
                            templateId: contentId,
                            senderProfileId: selectedTemplate.senderProfileId,
                            subjectLine: selectedTemplate.subjectLine ?? '',
                            preHeader: selectedTemplate.preHeader ?? '',
                        },
                        nodeId,
                    });
                }
            } else {
                // Clear all fields when no template selected
                editContent({
                    contentFields: {
                        ...contentFields,
                        templateId: null,
                        senderProfileId: null,
                        subjectLine: '',
                        preHeader: '',
                    },
                    nodeId,
                });
                setContentPreviewNodeId(null);
            }
        },
        [contentFields, editContent, nodeId, setContentPreviewNodeId, templates]
    );

    const handlePreviewChange = useCallback(() => {
        if (templateId) {
            if (contentPreviewNodeId === nodeId) {
                setContentPreviewNodeId(null);
            } else {
                setContentPreviewNodeId(nodeId);
            }
        }
    }, [contentPreviewNodeId, nodeId, setContentPreviewNodeId, templateId]);

    const subjectLineGeneratorHelperText = templateId
        ? `Click to use Cured's Subject Line Curator.`
        : `Select content for the Subject Line Generator.`;

    return (
        <Stack rowGap={4}>
            <ContentSelector
                contentType={CampaignChannels.EMAIL}
                contentId={templateId}
                contentList={templates}
                onContentChange={handleContentChange}
                isPreviewVisible={contentPreviewNodeId === nodeId}
                onPreviewChange={handlePreviewChange}
                isLoading={isLoadingTemplates}
            />
            {/* If the user clears the templateId from the Autocomplete, we don't want to hide the other fields if they have filled them out. */}
            <>
                <Select
                    data-test="curation-content-page-sender-profile-dropdown"
                    data-testid="curation-content-page-sender-profile-dropdown"
                    required
                    label={<InputLabel label="Sender profile" labelIconTooltipText="Who is sending this email?" />}
                    menuItemStyles={{ textTransform: 'none' }}
                    onChange={(e: any) => {
                        editContent({
                            contentFields: { ...contentFields, senderProfileId: e.target.value as number },
                            nodeId,
                        });
                    }}
                    value={senderProfileId ?? ''}
                    values={
                        !isSenderProfilesLoading
                            ? senderProfiles?.map((senderProfile: SenderProfile) => ({
                                  value: senderProfile.id,
                                  label: senderProfile.from_address,
                              }))
                            : []
                    }
                    fullWidth
                />
                <div style={{ position: 'relative' }}>
                    <TextField
                        counterCurrent={subjectLine?.length ?? 0}
                        counterMax={255}
                        data-test="curation-content-page-subject-line"
                        data-testid="curation-content-page-subject-line"
                        fullWidth
                        inputProps={{ maxLength: 255 }}
                        label={
                            <InputLabel
                                label="Subject line"
                                labelIconTooltipText="A descriptive overview of your message that gives contacts a reason to open."
                            />
                        }
                        value={subjectLine ?? ''}
                        withCounter
                        onChange={(e: any) => {
                            editContent({
                                contentFields: { ...contentFields, subjectLine: e.target.value },
                                nodeId,
                            });
                        }}
                    />
                    <IconButton
                        buttonWrapperCss={{
                            position: 'absolute',
                            right: '-50px',
                            top: '4px',
                        }}
                        disabled={!templateId}
                        handleClick={() => setIsSubjectLinesDialogOpen(true)}
                        Icon={AutoFixHigh}
                        title={subjectLineGeneratorHelperText}
                    />
                    <SubjectLinesDialog
                        isOpen={isSubjectLinesDialogOpen}
                        subjectLines={suggestedSubjectLines}
                        onClickSubjectLine={(index: number) => {
                            editContent({
                                contentFields: {
                                    ...contentFields,
                                    subjectLine: suggestedSubjectLines?.[index] ?? '',
                                },
                                nodeId,
                            });
                        }}
                        onClose={() => setIsSubjectLinesDialogOpen(false)}
                    />
                </div>
                <TextField
                    data-test="curation-content-page-preview"
                    data-testid="email-preview-text-input"
                    label={
                        <InputLabel
                            label="Preview text"
                            labelIconTooltipText="A quick glimpse into the content of your message that appears after the subject line."
                        />
                    }
                    value={preHeader ?? ''}
                    onChange={(e: any) => {
                        editContent({
                            contentFields: { ...contentFields, preHeader: e.target.value },
                            nodeId,
                        });
                    }}
                    inputProps={{ maxLength: 100 }}
                    withCounter
                    counterCurrent={preHeader?.length ?? 0}
                    counterMax={100}
                    fullWidth
                />
            </>
        </Stack>
    );
};

export default CurationContentForm;
